import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useEffect, type ReactNode } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useOrgStore } from '@/stores/orgStore';

// Initialize PostHog
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

if (POSTHOG_KEY && typeof window !== 'undefined') {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
  });
}

// Provider component that handles user identification
export function PostHogProvider({ children }: { children: ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const currentOrg = useOrgStore((state) => state.currentOrg);

  useEffect(() => {
    if (!POSTHOG_KEY) return;

    if (user) {
      // Identify the user
      posthog.identify(user.id, {
        email: user.email,
        name: user.user_metadata?.full_name,
      });

      // Set organization as a group
      if (currentOrg) {
        posthog.group('organization', currentOrg.id, {
          name: currentOrg.name,
          plan: currentOrg.subscription_plan || 'trial',
        });
      }
    } else {
      // Reset on logout
      posthog.reset();
    }
  }, [user, currentOrg]);

  if (!POSTHOG_KEY) {
    return <>{children}</>;
  }

  return <PHProvider client={posthog}>{children}</PHProvider>;
}

// Export posthog instance for direct usage
export { posthog };

// Helper function to track events
export function trackEvent(event: string, properties?: Record<string, any>) {
  if (POSTHOG_KEY) {
    posthog.capture(event, properties);
  }
}

// Common events to track
export const AnalyticsEvents = {
  // Auth
  USER_SIGNED_UP: 'user_signed_up',
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',

  // Assets
  ASSET_CREATED: 'asset_created',
  ASSET_UPDATED: 'asset_updated',
  ASSET_DELETED: 'asset_deleted',
  ASSET_VIEWED: 'asset_viewed',
  ASSETS_EXPORTED: 'assets_exported',
  ASSETS_IMPORTED: 'assets_imported',

  // Audits
  AUDIT_CREATED: 'audit_created',
  AUDIT_STARTED: 'audit_started',
  AUDIT_COMPLETED: 'audit_completed',

  // Reports
  REPORT_CREATED: 'report_created',
  REPORT_EXPORTED: 'report_exported',
  REPORT_SCHEDULED: 'report_scheduled',

  // Users
  USER_INVITED: 'user_invited',
  USER_ROLE_CHANGED: 'user_role_changed',

  // Subscription
  BILLING_PORTAL_OPENED: 'billing_portal_opened',
  SUBSCRIPTION_UPGRADED: 'subscription_upgraded',
} as const;
